import React from 'react';
import { Modal, Button } from 'antd';
import { CloseCircleFilled, WarningOutlined } from '@ant-design/icons';

function humanizeFieldName(key) {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Render teks dengan dukungan bold sederhana ala markdown: **teks**.
 * Backend menandai bagian penting (kode/nomor notice dll) dengan **...**,
 * di sini kita pecah jadi <strong> - pesan lain yang tidak punya ** tetap
 * tampil sebagai teks biasa, tidak ada efek samping ke error message lain.
 */
function renderWithBold(text) {
  const parts = String(text).split(/\*\*(.+?)\*\*/g);
  return parts.map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part));
}

const SILENT_FIELD_KEYS = ['non_field_errors', 'error', 'status', 'detail', 'message'];

/**
 * Ubah responseMsg mentah (string JSON / object DRF error / array / primitif)
 * jadi daftar { field, messages } yang siap ditampilkan rapi.
 */
export function parseErrorDetail(rawError) {
  let data = rawError;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch (e) {
      return [{ field: null, messages: [data] }];
    }
  }

  if (data == null) {
    return [{ field: null, messages: ['Terjadi kesalahan yang tidak diketahui.'] }];
  }

  if (Array.isArray(data)) {
    return [{ field: null, messages: data.map((item) => String(item)) }];
  }

  if (typeof data === 'object') {
    const entries = Object.entries(data);
    if (entries.length === 0) {
      return [{ field: null, messages: ['Terjadi kesalahan yang tidak diketahui.'] }];
    }
    return entries.map(([key, value]) => {
      const messages = Array.isArray(value) ? value.map(String) : [String(value)];
      const field = SILENT_FIELD_KEYS.includes(key) ? null : humanizeFieldName(key);
      return { field, messages };
    });
  }

  return [{ field: null, messages: [String(data)] }];
}

/**
 * Popup error modern pengganti <Alert> inline yang menampilkan JSON mentah.
 * Dipakai dengan state {errTitle, errMsg} yang sudah ada di form-form lama -
 * cukup render <ErrorAlertModal visible={!!errTitle} title={errTitle} error={errMsg} onClose={...} />.
 */
export default function ErrorAlertModal({ visible, title, error, onClose, zIndex }) {
  const groups = parseErrorDetail(error);
  const totalMessages = groups.reduce((acc, g) => acc + g.messages.length, 0);

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      centered
      footer={null}
      closable={false}
      width="min(92vw, 440px)"
      bodyStyle={{ padding: 0 }}
      maskClosable
      // Default antd Modal zIndex (1000) sama untuk semua modal - kalau
      // dipanggil sementara modal lain (mis. modal konfirmasi alokasi box)
      // masih terbuka, urutan tumpukan jadi tidak terjamin selalu di atas.
      // Caller bisa kirim zIndex lebih tinggi utk kasus begini.
      zIndex={zIndex}
      className="error-alert-modal"
    >
      <style>{`
        .error-alert-modal .ant-modal-content {
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 16px 48px rgba(0,0,0,0.25);
        }
      `}</style>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          padding: '22px 24px 18px',
          background: 'linear-gradient(135deg, #b91c1c, #ef4444)',
        }}
      >
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.18)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <CloseCircleFilled style={{ color: '#fff', fontSize: 22 }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>
            {title || 'Gagal Menyimpan Data'}
          </div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', marginTop: 2 }}>
            {totalMessages > 1 ? `${totalMessages} hal perlu diperbaiki` : 'Periksa kembali data Anda'}
          </div>
        </div>
      </div>

      <div style={{ padding: '20px 24px 4px' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 4 }}>
          <WarningOutlined style={{ color: '#ef4444', fontSize: 15, marginTop: 3, flexShrink: 0 }} />
          <ul style={{ paddingLeft: 16, margin: 0, flex: 1 }}>
            {groups.map((group, idx) =>
              group.messages.map((msg, mi) => (
                <li
                  key={`${idx}-${mi}`}
                  style={{ color: '#4b5563', fontSize: 13.5, marginBottom: 8, lineHeight: 1.55, wordBreak: 'break-word' }}
                >
                  {group.field ? <strong style={{ color: '#262626' }}>{group.field}: </strong> : null}
                  {renderWithBold(msg)}
                </li>
              ))
            )}
          </ul>
        </div>
      </div>

      <div
        style={{
          padding: '14px 24px 20px',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          type="primary"
          danger
          onClick={onClose}
          style={{ borderRadius: 8, fontWeight: 600, minWidth: 96 }}
        >
          Tutup
        </Button>
      </div>
    </Modal>
  );
}
